import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/Kpi";
import { inputCls } from "@/components/Filters";
import { api, apiError } from "@/lib/api";
import { eur, dataOraIt, dateIt, num } from "@/lib/format";

const bottone = "rounded-md px-4 py-2 text-sm transition-colors duration-200";

const CAMPI_GIORNI = [
  ["giorni_lavorati_anno", "Giorni lavorati anno (a oggi)"],
  ["giorni_lavorativi_anno", "Giorni lavorativi anno"],
  ["giorni_lavorati_quarter", "Giorni lavorati quarter in corso (a oggi)"],
  ["giorni_lavorativi_quarter", "Giorni lavorativi quarter in corso"],
  ["giorni_lavorati_mese", "Giorni lavorati mese in corso (a oggi)"],
  ["giorni_lavorativi_mese", "Giorni lavorativi mese in corso"],
];

/** Interruttori di visualizzazione della dashboard corto. */
export function VisualizzazioneCorto() {
  const [attivo, setAttivo] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => {
      const c = (r.data.rows || []).find((x) => x.key === "corto_mostra_netto_erogato");
      setAttivo(!!c?.value);
    }).catch(() => setAttivo(false));
  }, []);

  const cambia = async (valore) => {
    setAttivo(valore);
    try {
      await api.put("/config", { key: "corto_mostra_netto_erogato", value: valore });
      toast.success(valore ? "Box Netto erogato mostrati" : "Box Netto erogato nascosti");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (attivo === null) return null;

  return (
    <Card className="p-6" data-testid="config-corto-visualizzazione">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Visualizzazione dashboard corto
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Scegli quali blocchi mostrare nella dashboard del canale corto.
      </p>
      <label className="flex items-center gap-3 text-sm cursor-pointer">
        <input type="checkbox" checked={attivo} data-testid="corto-mostra-netto-erogato"
          onChange={(e) => cambia(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 accent-[#0018A8]" />
        Mostra i box "Netto erogato" (anno e quarter in corso)
      </label>
    </Card>
  );
}

/** Percentuale di rischio del canale corto: dato inserito manualmente. */
export function RischioCorto() {
  const [valore, setValore] = useState(null);
  const [agenzie, setAgenzie] = useState([]);
  const [perAgenzia, setPerAgenzia] = useState({});
  const [target, setTarget] = useState("");
  const [targetAgenzie, setTargetAgenzie] = useState({});

  useEffect(() => {
    api.get("/config").then((r) => {
      const rows = r.data.rows || [];
      const c = rows.find((x) => x.key === "corto_rischio");
      setValore(c?.value ?? 3.75);
      setPerAgenzia(rows.find((x) => x.key === "corto_rischio_agenzie")?.value || {});
      setTarget(rows.find((x) => x.key === "corto_rischio_target")?.value ?? "");
      setTargetAgenzie(rows.find((x) => x.key === "corto_rischio_target_agenzie")?.value || {});
    }).catch(() => setValore(3.75));
    api.get("/corto/agenzie").then((r) => setAgenzie(r.data.rows || [])).catch(() => setAgenzie([]));
  }, []);

  const salva = async () => {
    try {
      await api.put("/config", { key: "corto_rischio", value: parseFloat(valore) });
      const pulisci = (obj) => Object.fromEntries(Object.entries(obj)
        .filter(([, v]) => v !== "" && v != null && !Number.isNaN(parseFloat(v)))
        .map(([k, v]) => [k, parseFloat(v)]));
      await api.put("/config", { key: "corto_rischio_agenzie", value: pulisci(perAgenzia) });
      await api.put("/config", { key: "corto_rischio_target",
        value: target === "" || target == null ? null : parseFloat(target) });
      await api.put("/config", { key: "corto_rischio_target_agenzie", value: pulisci(targetAgenzie) });
      toast.success("Rischio aggiornato");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (valore === null) return null;

  return (
    <Card className="p-6" data-testid="config-corto-rischio">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Rischio · canale corto
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Percentuale di rischio mostrata nel box della dashboard corto. È un dato comunicato dalla
        direzione: scrivilo qui e si aggiorna in dashboard.
      </p>
      <div className="flex items-end gap-4 flex-wrap">
        <div className="w-48">
          <label className="text-xs text-slate-500 block mb-1">Rischio DBE (%)</label>
          <input className={inputCls} type="number" step="0.01" min="0" value={valore}
            data-testid="corto-rischio-input" onChange={(e) => setValore(e.target.value)} />
        </div>
        <div className="w-48">
          <label className="text-xs text-slate-500 block mb-1">Target DBE (%)</label>
          <input className={inputCls} type="number" step="0.01" min="0" value={target ?? ""} placeholder="n/d"
            data-testid="corto-rischio-target" onChange={(e) => setTarget(e.target.value)} />
        </div>
        <div className="text-xs text-slate-500 pb-2">In obiettivo quando il rischio è <b>sotto</b> il target.</div>
      </div>
      <div className="mt-6">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
          Rischio e target per agenzia (%)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="corto-rischio-agenzie">
          {agenzie.map((a) => (
            <div key={a.slug} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4"
              data-testid={`corto-rischio-box-${a.slug}`}>
              <div className="text-sm font-semibold text-slate-800 mb-3">{a.agenzia}</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Rischio (%)</label>
                  <input className={inputCls} type="number" step="0.01" min="0" placeholder="n/d"
                    value={perAgenzia[a.agenzia] ?? ""} data-testid={`corto-rischio-${a.slug}`}
                    onChange={(e) => setPerAgenzia((s) => ({ ...s, [a.agenzia]: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Target (%)</label>
                  <input className={inputCls} type="number" step="0.01" min="0" placeholder="n/d"
                    value={targetAgenzie[a.agenzia] ?? ""} data-testid={`corto-rischio-target-${a.slug}`}
                    onChange={(e) => setTargetAgenzie((s) => ({ ...s, [a.agenzia]: e.target.value }))} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <button onClick={salva} data-testid="corto-rischio-salva"
        className={`${bottone} mt-5 bg-slate-900 text-white hover:bg-slate-800`}>
        Salva rischio
      </button>
    </Card>
  );
}

/** Caricamento del file Excel della redditività (classifiche canale corto). */
export function ContoCartaCorto() {
  const [meta, setMeta] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const carica = () => api.get("/corto/conto-carta?anno=2026").then((r) => setMeta(r.data.meta || {})).catch(() => setMeta({}));
  useEffect(() => { carica(); }, []);
  const invia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttesa(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/corto/conto-carta/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`DB Conto Carta importati · ${num(r.data.righe)} (anni ${r.data.anni.join(", ")})`);
      carica();
    } catch (err) { toast.error(apiError(err)); } finally {
      setAttesa(false);
      e.target.value = "";
    }
  };
  if (!meta) return null;
  return (
    <Card className="p-6" data-testid="config-corto-conto-carta">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">DB Conto Carta · canale corto</h3>
      <p className="text-sm text-slate-500 mb-4">
        Carica il file "DB CONTO CARTA estrazione AAAA.xlsx": vengono letti i fogli di riepilogo mensile per agenzia
        (nome foglio = anno). Alimenta l'istogramma DB Conto Carta della dashboard e delle schede agenzia.
        Ogni caricamento sostituisce integralmente i dati precedenti.
      </p>
      <label className={`inline-block cursor-pointer ${bottone} bg-slate-900 text-white hover:bg-slate-800 ${attesa ? "opacity-50 pointer-events-none" : ""}`}>
        {attesa ? "Importazione…" : "Carica file DB Conto Carta"}
        <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={invia} data-testid="corto-conto-carta-file" />
      </label>
      <div className="mt-4 text-xs text-slate-500" data-testid="corto-conto-carta-stato">
        {meta.file ? <>Ultimo file: <span className="font-mono">{meta.file}</span> · {num(meta.righe)} DB CC
          {meta.aggiornato_il ? ` · ${dateIt(meta.aggiornato_il)}` : ""}</> : "Nessun file caricato."}
      </div>
    </Card>
  );
}


export function RedditivitaCorto() {
  const [meta, setMeta] = useState(null);
  const [attesa, setAttesa] = useState(false);

  const carica = () => api.get("/corto/redditivita")
    .then((r) => setMeta({ ...r.data.meta, media_dbe: r.data.media_dbe }))
    .catch(() => setMeta({}));

  useEffect(() => { carica(); }, []);

  const invia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttesa(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/corto/redditivita/import", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Redditività importata · ${r.data.righe} righe`);
      carica();
    } catch (err) { toast.error(apiError(err)); } finally {
      setAttesa(false);
      e.target.value = "";
    }
  };

  if (!meta) return null;

  return (
    <Card className="p-6" data-testid="config-corto-redditivita">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Redditività · canale corto
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Carica il file "FOGLI REDDITIVITA' 2026.xlsx". Serve per il box Redditività della dashboard
        (media DBE) e per le tre classifiche nella pagina Classifiche. Ogni caricamento sostituisce
        integralmente i dati precedenti.
      </p>
      <label className={`inline-block cursor-pointer ${bottone} bg-slate-900 text-white
        hover:bg-slate-800 ${attesa ? "opacity-50 pointer-events-none" : ""}`}>
        {attesa ? "Importazione…" : "Carica file redditività"}
        <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={invia}
          data-testid="corto-redditivita-file" />
      </label>
      <div className="mt-4 text-xs text-slate-500 space-y-1" data-testid="corto-redditivita-stato">
        {meta.file ? (
          <>
            <div>Ultimo file: <span className="font-mono">{meta.file}</span> · {meta.righe} righe
              {meta.aggiornato_il ? ` · ${dateIt(meta.aggiornato_il)}` : ""}</div>
            <div>Media DBE in uso: <span className="font-mono">{num(meta.media_dbe, 2)}%</span></div>
          </>
        ) : <div>Nessun file caricato.</div>}
      </div>
    </Card>
  );
}

const VOCI_BUDGET = [["non_garantito", "Non garantito"], ["garantito", "Garantito"],
  ["vivibanca", "Vivibanca"], ["totale", "Totale"]];
const MESI_BUDGET = [["1", "Gen"], ["2", "Feb"], ["3", "Mar"], ["4", "Apr"], ["5", "Mag"], ["6", "Giu"],
  ["7", "Lug"], ["8", "Ago"], ["9", "Set"], ["10", "Ott"], ["11", "Nov"], ["12", "Dic"]];
const QUARTER = [1, 2, 3, 4];
const sommaQuarter = (mesi, q) => [1, 2, 3].reduce((s, i) => s + (parseFloat(mesi?.[String(3 * (q - 1) + i)]) || 0), 0);

/** Budget annuale delle singole agenzie del canale corto. */
export function BudgetAgenzieCorto() {
  const [agenzie, setAgenzie] = useState([]);
  const [valori, setValori] = useState({});
  const [budgetBanca, setBudgetBanca] = useState({ totale: "37100000", non_garantito: "33800000", garantito: "3300000" });

  useEffect(() => {
    api.get("/corto/agenzie").then((r) => setAgenzie(r.data.rows || [])).catch(() => setAgenzie([]));
    api.get("/config").then((r) => {
      const c = (r.data.rows || []).find((x) => x.key === "corto_budget_agenzie");
      setValori(c?.value || {});
      const b = (r.data.rows || []).find((x) => x.key === "corto_budget_banca");
      if (b?.value && typeof b.value === "object") {
        setBudgetBanca({ totale: String(b.value.totale ?? "37100000"), non_garantito: String(b.value.non_garantito ?? "33800000"),
          garantito: String(b.value.garantito ?? "3300000") });
      }
    }).catch(() => setValori({}));
  }, []);

  const salvaBanca = async () => {
    try {
      await api.put("/config", { key: "corto_budget_banca", value: {
        totale: parseFloat(budgetBanca.totale) || null, non_garantito: parseFloat(budgetBanca.non_garantito) || null,
        garantito: parseFloat(budgetBanca.garantito) || null } });
      toast.success("Budget banca salvato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const cambia = (agenzia, voce, v) => setValori({
    ...valori, [agenzia]: { ...(valori[agenzia] || {}), [voce]: v },
  });

  const cambiaMese = (agenzia, mese, v) => setValori({
    ...valori,
    [agenzia]: { ...(valori[agenzia] || {}),
      mesi: { ...((valori[agenzia] || {}).mesi || {}), [mese]: v } },
  });

  const salva = async () => {
    try {
      await api.put("/config", { key: "corto_budget_agenzie", value: valori });
      toast.success("Budget agenzie salvati");
    } catch (e) { toast.error(apiError(e)); }
  };

  const nomi = agenzie.map((a) => a.agenzia);
  const sommaVoce = (voce) => nomi.reduce((s, n) => s + (parseFloat(valori[n]?.[voce]) || 0), 0);
  const sommaMese = (mese) => nomi.reduce((s, n) => s + (parseFloat(valori[n]?.mesi?.[mese]) || 0), 0);
  const totale = sommaVoce("totale");

  if (!agenzie.length) return null;

  return (
    <Card className="p-6" data-testid="config-corto-budget">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Budget annuale · agenzie canale corto
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Budget 2026 di liquidato per agenzia. I tachimetri confrontano la proiezione a fine anno con il
        budget di totale, non garantito e garantito (Vivibanca serve solo a comporre il totale). I budget
        dei <strong>quarter</strong> si calcolano da soli come somma dei tre mesi (gen-feb-mar = Q1, apr-mag-giu = Q2…).
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-5 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="config-corto-budget-banca">
        {[["totale", "Totale"], ["non_garantito", "Non garantito"], ["garantito", "Garantito"]].map(([k, label]) => (
          <div key={k}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Budget banca · {label} (€)</div>
            <input type="number" step="100000" value={budgetBanca[k]} onChange={(e) => setBudgetBanca({ ...budgetBanca, [k]: e.target.value })}
              className="w-44 rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid={`corto-budget-banca-${k}`} />
          </div>
        ))}
        <button type="button" onClick={salvaBanca} data-testid="corto-budget-banca-salva"
          className="px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800">Salva budget banca</button>
        <div className="text-xs text-slate-500 max-w-xl">
          Usato dai tachimetri "Anno · totale / non garantito / garantito" e dai box "% raggiungimento budget" e
          "Previsione chiusura anno" della Dashboard totale (non dai budget per agenzia).
          {totale ? ` Somma budget agenzie: ${eur(totale)}.` : ""}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 text-left">Agenzia</th>
              {VOCI_BUDGET.map(([, label]) => (
                <th key={label} className="py-2 px-2 text-right font-medium">{label}</th>
              ))}
              {MESI_BUDGET.map(([mese, label]) => (
                <th key={label} className={`py-2 px-2 text-right font-medium ${(parseInt(mese, 10) - 1) % 3 === 0 ? "border-l border-slate-200" : ""}`}>{label}</th>
              ))}
              {QUARTER.map((q) => (
                <th key={q} className="py-2 px-2 text-right font-medium text-[#0018A8] border-l border-slate-200">Q{q}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agenzie.map((a) => (
              <tr key={a.slug} className="border-b border-slate-100">
                <td className="py-2 pr-3 font-medium whitespace-nowrap">{a.agenzia}</td>
                {VOCI_BUDGET.map(([voce]) => (
                  <td key={voce} className="py-1 px-1">
                    <input className={`${inputCls} text-right`} type="number" min="0" step="10000"
                      placeholder="0" data-testid={`corto-budget-${a.slug}-${voce}`}
                      value={valori[a.agenzia]?.[voce] ?? ""}
                      onChange={(e) => cambia(a.agenzia, voce, e.target.value)} />
                  </td>
                ))}
                {MESI_BUDGET.map(([mese]) => (
                  <td key={mese} className={`py-1 px-1 ${(parseInt(mese, 10) - 1) % 3 === 0 ? "border-l border-slate-200" : ""}`}>
                    <input className={`${inputCls} text-right min-w-[96px]`} type="number" min="0" step="10000"
                      placeholder="0" data-testid={`corto-budget-${a.slug}-mese-${mese}`}
                      value={valori[a.agenzia]?.mesi?.[mese] ?? ""}
                      onChange={(e) => cambiaMese(a.agenzia, mese, e.target.value)} />
                  </td>
                ))}
                {QUARTER.map((q) => (
                  <td key={q} className="py-1 px-2 text-right font-mono text-xs text-[#0018A8] border-l border-slate-200 whitespace-nowrap"
                    data-testid={`corto-budget-${a.slug}-quarter-${q}`}>{eur(sommaQuarter(valori[a.agenzia]?.mesi, q))}</td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="corto-budget-riga-totali">
              <td className="py-2 pr-3 whitespace-nowrap">Totale agenzie</td>
              {VOCI_BUDGET.map(([voce]) => (
                <td key={voce} className="py-2 px-2 text-right font-mono text-xs"
                  data-testid={`corto-budget-totale-${voce}`}>{eur(sommaVoce(voce))}</td>
              ))}
              {MESI_BUDGET.map(([mese]) => (
                <td key={mese} className="py-2 px-2 text-right font-mono text-xs"
                  data-testid={`corto-budget-totale-mese-${mese}`}>{eur(sommaMese(mese))}</td>
              ))}
              {QUARTER.map((q) => (
                <td key={q} className="py-2 px-2 text-right font-mono text-xs text-[#0018A8] border-l border-slate-200"
                  data-testid={`corto-budget-totale-quarter-${q}`}>{eur(nomi.reduce((s, n) => s + sommaQuarter(valori[n]?.mesi, q), 0))}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <button onClick={salva} data-testid="corto-budget-salva"
          className={`${bottone} bg-slate-900 text-white hover:bg-slate-800`}>
          Salva budget agenzie
        </button>
        <span className="text-xs text-slate-500 font-mono" data-testid="corto-budget-totale">
          Totale budget agenzie: {eur(totale)}
        </span>
      </div>
    </Card>
  );
}

/** Giorni lavorativi del canale corto: indipendenti da quelli del canale lungo. */
export function GiorniCorto() {
  const [valori, setValori] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/corto/giorni"), api.get("/config")]).then(([g, r]) => {
      const c = (r.data.rows || []).find((x) => x.key === "corto_giorni");
      setValori({ ...g.data, ...(c?.value || {}) });
    }).catch(() => setValori(null));
  }, []);

  const salva = async () => {
    try {
      await api.put("/config", { key: "corto_giorni", value: valori });
      toast.success("Giorni lavorativi del canale corto salvati · proiezioni ricalcolate");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!valori) return null;

  return (
    <Card className="p-6" data-testid="config-corto-giorni">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Giorni lavorativi · Canale corto
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Le proiezioni del canale corto (anno e quarter in corso) sono calcolate come
        liquidato ÷ giorni lavorati × giorni lavorativi. I giorni sono calcolati automaticamente dal
        calendario (esclusi sabati, domeniche e festivi) e i giorni lavorati avanzano da soli ogni
        giorno: salva qui solo se vuoi forzare valori diversi.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {CAMPI_GIORNI.map(([k, label]) => (
          <div key={k}>
            <label className="text-xs text-slate-500 block mb-1">{label}</label>
            <input className={inputCls} type="number" min="1" value={valori[k]}
              data-testid={`corto-${k.replace(/_/g, "-")}`}
              onChange={(e) => setValori({ ...valori, [k]: e.target.value })} />
          </div>
        ))}
      </div>
      <button onClick={salva} data-testid="corto-giorni-salva"
        className={`mt-4 ${bottone} bg-slate-900 text-white hover:bg-slate-800`}>
        Salva giorni canale corto
      </button>
    </Card>
  );
}

/** Collegamento al CRM Databeasy: credenziali, prova e sincronizzazione delle pratiche. */
export function CollegamentoCrm() {
  const [stato, setStato] = useState(null);
  const [form, setForm] = useState({ username: "", password: "", base_url: "" });
  const [attesa, setAttesa] = useState("");

  const carica = () => api.get("/crm/stato").then((r) => {
    setStato(r.data);
    setForm((f) => ({ ...f, username: r.data.username || "", base_url: r.data.base_url || "" }));
  }).catch(() => setStato({ configurato: false }));

  useEffect(() => {
    carica();
    // credenziali e stato vivono sul server: ogni dispositivo aperto si riallinea da solo
    const t = setInterval(carica, 30000);
    window.addEventListener("focus", carica);
    return () => { clearInterval(t); window.removeEventListener("focus", carica); };
  }, []);

  const salva = async () => {
    setAttesa("salva");
    try {
      const r = await api.put("/crm/credenziali", form);
      setForm((f) => ({ ...f, password: "" }));
      toast.success(r.data.verificate ? "Credenziali verificate sul CRM e salvate" : "Credenziali CRM salvate");
      carica();
    } catch (e) { toast.error(apiError(e)); } finally { setAttesa(""); }
  };

  const prova = async () => {
    setAttesa("prova");
    try {
      const r = await api.post("/crm/prova");
      toast.success(`Collegamento riuscito · ${r.data.filiali} filiali trovate`);
      carica();
    } catch (e) { toast.error(apiError(e)); carica(); } finally { setAttesa(""); }
  };

  const sincronizza = async () => {
    setAttesa("sync");
    try {
      const r = await api.post("/crm/sincronizza");
      toast.success(`Sincronizzate ${num(r.data.pratiche)} pratiche da ${r.data.filiali} filiali`);
      carica();
    } catch (e) { toast.error(apiError(e)); carica(); } finally { setAttesa(""); }
  };

  if (!stato) return null;

  return (
    <Card className="p-6" data-testid="config-crm">
      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
        Collegamento CRM · Canale corto
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Il sito legge le pratiche dei prestiti personali direttamente dal CRM. Inserisci le
        credenziali del CRM (non quelle di Google): restano sul server, cifrate, e non vengono mai
        mostrate. La password del CRM scade ogni 30 giorni: quando accade, riscrivila qui.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Nome utente CRM</label>
          <input className={inputCls} value={form.username} data-testid="crm-username"
            autoComplete="off" onChange={(e) => setForm({ ...form, username: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">
            Password CRM {stato.configurato && "(lascia vuoto per non cambiarla)"}
          </label>
          <input className={inputCls} type="password" value={form.password} data-testid="crm-password"
            autoComplete="new-password"
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Indirizzo API</label>
          <input className={inputCls} value={form.base_url} data-testid="crm-base-url"
            onChange={(e) => setForm({ ...form, base_url: e.target.value })} />
        </div>
      </div>
      <div className="mt-4 flex gap-3 flex-wrap">
        <button onClick={salva} disabled={!!attesa} data-testid="crm-salva"
          className={`${bottone} bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50`}>
          {attesa === "salva" ? "Salvataggio…" : "Salva credenziali"}
        </button>
        <button onClick={prova} disabled={!!attesa || !stato.configurato} data-testid="crm-prova"
          className={`${bottone} border border-slate-200 text-slate-700 hover:border-slate-900 disabled:opacity-50`}>
          {attesa === "prova" ? "Verifica…" : "Prova collegamento"}
        </button>
        <button onClick={sincronizza} disabled={!!attesa || !stato.configurato} data-testid="crm-sincronizza"
          className={`${bottone} bg-[#0018A8] text-white hover:bg-[#001480] disabled:opacity-50`}>
          {attesa === "sync" ? "Sincronizzazione in corso…" : "Aggiorna dal CRM"}
        </button>
      </div>
      <div className="mt-4 text-xs text-slate-500 space-y-1" data-testid="crm-stato">
        <div>
          Stato: {stato.configurato
            ? <span className="text-emerald-600 font-semibold">credenziali presenti</span>
            : <span className="text-amber-600 font-semibold">da configurare</span>}
          {stato.password_aggiornata_il && ` · password inserita il ${dateIt(stato.password_aggiornata_il)}`}
        </div>
        <div>
          Pratiche già sincronizzate: <span className="font-mono"
            data-testid="crm-pratiche-sincronizzate">{num(stato.pratiche_sincronizzate)}</span>
          {stato.ultima_sincronizzazione && (
            <span data-testid="crm-ultimo-aggiornamento">
              {` · ultimo aggiornamento ${dataOraIt(stato.ultima_sincronizzazione)}`}
              {stato.ultimo_esito && ` · ${num(stato.ultimo_esito.nuove)} nuove pratiche`}
            </span>
          )}
        </div>
        <div>Aggiornamento automatico ogni ora dalle 8:00 alle 19:00 nei giorni lavorativi
          (esclusi sabato, domenica e festivi) e ogni notte alle 02:20. Se il pianificatore non
          chiama, un controllo interno ogni 10 minuti avvia comunque la sincronizzazione quando
          l'ultima è più vecchia di 65 minuti.</div>
        {stato.ultimo_errore?.messaggio && (
          <div className="text-red-600" data-testid="crm-ultimo-errore">
            Ultimo errore: {stato.ultimo_errore.messaggio}
          </div>
        )}
      </div>
      <DiagnosticaCron />
    </Card>
  );
}

function DiagnosticaCron() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/crm/cron-diagnostica").then((r) => setD(r.data)).catch(() => setD(null));
  }, [open]);
  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="crm-diagnostica-toggle"
        className="text-xs font-semibold text-slate-500 hover:text-[#0018A8] transition-colors duration-200">
        {open ? "Nascondi" : "Mostra"} diagnostica pianificatore (ultime chiamate automatiche)
      </button>
      {open && d && (
        <div className="mt-2 text-xs" data-testid="crm-diagnostica">
          <div className="text-slate-500 mb-1">
            Chiave del pianificatore configurata: <b>{d.secret_configurato ? "sì" : "NO"}</b>
          </div>
          {d.chiamate.length === 0 ? (
            <div className="text-amber-700">Nessuna chiamata automatica registrata finora.</div>
          ) : (
            <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100">
              {d.chiamate.map((c, i) => (
                <li key={i} className="py-1 flex gap-3">
                  <span className="text-slate-400 w-32 shrink-0">{dataOraIt(c.ts)}</span>
                  <span className="font-mono">{c.cron}</span>
                  <span className={c.esito.startsWith("rifiutata") ? "text-red-600" : "text-emerald-700"}>{c.esito}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


/** Elenco operatori CRM: scegli chi mostrare nella redemption per operatore. */
export function OperatoriCorto() {
  const [righe, setRighe] = useState(null);
  const [cerca, setCerca] = useState("");

  useEffect(() => {
    api.get("/corto/operatori").then((r) => setRighe(r.data.operatori || [])).catch(() => setRighe([]));
  }, []);

  const cambia = async (nome, visibile) => {
    setRighe((r) => r.map((o) => (o.nome === nome ? { ...o, visibile } : o)));
    try {
      await api.put("/corto/operatori/visibilita", { nome, visibile });
      toast.success(visibile ? `${nome}: redemption mostrata` : `${nome}: redemption nascosta`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (righe === null) return null;
  const v = cerca.trim().toLowerCase();
  const lista = righe.filter((o) => !v || o.nome.toLowerCase().includes(v));
  const nascosti = righe.filter((o) => !o.visibile).length;
  return (
    <Card className="p-6" data-testid="config-corto-operatori">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">Operatori · redemption</h3>
          <p className="text-sm text-slate-500">
            Scegli per quali operatori mostrare la redemption nella dashboard corto. {righe.length} operatori letti dal CRM{nascosti ? `, ${nascosti} nascosti` : ""}.
          </p>
        </div>
        <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca operatore…" data-testid="config-corto-operatori-cerca"
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 w-52" />
      </div>
      {!righe.length ? (
        <div className="text-sm text-slate-400" data-testid="config-corto-operatori-vuoto">Nessuna telefonata letta dal CRM: l'elenco si popola dopo la prima lettura.</div>
      ) : (
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="config-corto-operatori-tabella">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Mostra</th><th className="py-2 pr-3">Operatore</th><th className="py-2 pr-3">Agenzie</th>
                <th className="py-2 pr-3 text-right">Chiamate</th><th className="py-2 pr-3 text-right">Risposte</th><th className="py-2 pr-3">Ultima</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((o) => (
                <tr key={o.nome} className={`border-b border-slate-100 ${o.visibile ? "" : "text-slate-400"}`} data-testid={`config-corto-operatore-${o.nome.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <td className="py-2 pr-3">
                    <input type="checkbox" checked={o.visibile} onChange={(e) => cambia(o.nome, e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 accent-[#0018A8]" data-testid={`config-corto-operatore-check-${o.nome.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                  </td>
                  <td className="py-2 pr-3 font-medium">{o.nome}</td>
                  <td className="py-2 pr-3 text-xs">{o.agenzie.join(", ")}</td>
                  <td className="py-2 pr-3 text-right font-mono">{num(o.chiamate)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{num(o.risposte)}</td>
                  <td className="py-2 pr-3 text-xs">{dateIt(o.ultimo_giorno)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
